<template>
  <div class="flex flex-col h-full bg-gray-900">
    <!-- Show conversation thread if one is selected -->
    <ConversationThread
      v-if="activeConversationId"
      :conversation-id="activeConversationId"
      :show-back-button="true"
      @back="activeConversationId = null"
      @close="$emit('close')" />

    <!-- Show conversations list -->
    <template v-else>
      <!-- Header -->
      <div
        class="flex justify-between items-center py-3 px-4 border-b border-gray-800">
        <h2 class="text-lg font-semibold text-white">Messages</h2>
        <button
          class="p-2 -mr-2 text-gray-400 transition-colors hover:text-white"
          @click="$emit('close')">
          <fa-icon icon="xmark" />
        </button>
      </div>

      <!-- Content -->
      <div class="overflow-y-auto flex-1">
        <!-- Loading state -->
        <div v-if="isLoading" class="flex justify-center py-8">
          <fa-icon icon="spinner-third" spin class="text-2xl text-gray-500" />
        </div>

        <!-- Empty state -->
        <div v-else-if="conversations.length === 0" class="p-6 text-center">
          <div
            class="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gray-800 rounded-full">
            <fa-icon
              :icon="['far', 'paper-plane']"
              class="text-2xl text-gray-500" />
          </div>
          <p class="text-sm text-gray-400">No messages yet</p>
          <p class="mt-1 text-xs text-gray-500">
            Share a review or title with friends to start a conversation
          </p>
        </div>

        <!-- Conversations list -->
        <div v-else class="divide-y divide-gray-800">
          <button
            v-for="conv in conversations"
            :key="conv.id"
            class="w-full px-4 py-3 hover:bg-gray-800/50 transition-colors text-left"
            @click="openConversation(conv.id)">
            <UserBlock
              :user="getOtherParticipant(conv)"
              size="lg"
              :link-to-profile="false"
              :show-username="false">
              <template #subtitle>
                <!-- Content type indicator -->
                <p class="text-sm text-gray-400 truncate">
                  <template v-if="conv.review">
                    Review: {{ conv.review.title_title }}
                  </template>
                  <template v-else-if="conv.title">
                    {{ conv.title.title }}
                  </template>
                  <template v-else-if="conv.list">
                    List: {{ conv.list.title }}
                  </template>
                </p>

                <!-- Last message preview -->
                <p
                  v-if="conv.last_message_preview"
                  class="text-xs text-gray-500 truncate">
                  {{ conv.last_message_preview }}
                </p>
              </template>

              <template #right>
                <div class="flex flex-col gap-1 items-end shrink-0">
                  <span class="text-xs text-gray-500">
                    {{ formatTime(conv.last_message_at || conv.created_at) }}
                  </span>
                  <!-- Unread indicator -->
                  <div
                    v-if="conv.unread_count > 0"
                    class="flex justify-center items-center w-5 h-5 bg-indigo-500 rounded-full">
                    <span class="text-[10px] font-semibold text-white">
                      {{ conv.unread_count > 9 ? '9+' : conv.unread_count }}
                    </span>
                  </div>
                </div>
              </template>
            </UserBlock>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStoreMessages } from '@/stores/storeMessages'
import { useStoreAuth } from '@/stores/storeAuth'
import UserBlock from '@/components/UserBlock.vue'
import ConversationThread from '@/components/Messages/ConversationThread.vue'

const props = defineProps({
  initialConversationId: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['close'])

const storeMessages = useStoreMessages()
const storeAuth = useStoreAuth()

const activeConversationId = ref(props.initialConversationId)

const conversations = computed(() => storeMessages.conversations)
const isLoading = computed(() => storeMessages.isLoading)
const currentUserId = computed(() => storeAuth.user?.id)

const getOtherParticipant = (conversation) => {
  if (!conversation?.participants) return null
  return conversation.participants.find(
    (p) => p.user_id !== currentUserId.value
  )
}

const openConversation = (conversationId) => {
  activeConversationId.value = conversationId
}

const formatTime = (dateString) => {
  if (!dateString) return ''

  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  if (diffDays === 1) return 'Yesterday'

  if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' })
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  })
}

// Load conversations on mount
onMounted(() => {
  storeMessages.fetchConversations()
})

// Watch for initial conversation ID changes
watch(
  () => props.initialConversationId,
  (newId) => {
    if (newId) {
      activeConversationId.value = newId
    }
  }
)
</script>
